import React, { useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import { Bell, Send, Smartphone, Users, RefreshCw, ChevronDown, ChevronRight, AlertTriangle, CheckCircle2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import EmptyState, { LoadingState } from "../../components/EmptyState";

const personaOptions = [
  { value: "all", label: "All personas" },
  { value: "customer", label: "Customers" },
  { value: "rider", label: "Riders" },
  { value: "admin", label: "Tenant admins" },
];
const platformOptions = [
  { value: "all", label: "All platforms" },
  { value: "android", label: "Android" },
  { value: "ios", label: "iOS" },
];

export default function SuperPush() {
  const [tenants, setTenants] = useState([]);
  const [slug, setSlug] = useState("");
  const [persona, setPersona] = useState("all");
  const [platform, setPlatform] = useState("all");
  const [userPhone, setUserPhone] = useState("");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [deepLink, setDeepLink] = useState("");
  const [tokensSummary, setTokensSummary] = useState(null);
  const [logs, setLogs] = useState([]);
  const [sending, setSending] = useState(false);
  const [expandedLog, setExpandedLog] = useState(null);
  const [diagnosis, setDiagnosis] = useState(null);
  const [diagnosing, setDiagnosing] = useState(false);

  // Load tenant list once
  useEffect(() => {
    let cancelled = false;
    api.get("/api/super-admin/tenants").then(({ data }) => {
      if (cancelled) return;
      const list = data.tenants || [];
      setTenants(list);
      // Default to "__all__" so operators see EVERY registered device — easy
      // to spot devices that registered against the wrong tenant_id.
      setSlug((cur) => cur || "__all__");
    }).catch(() => toast.error("Failed to load tenants"));
    return () => { cancelled = true; };
  }, []);

  const loadTokens = async (forSlug) => {
    if (!forSlug) return;
    try {
      const { data } = await api.get(`/api/super-admin/push/tokens?tenant=${encodeURIComponent(forSlug)}`);
      setTokensSummary(data);
    } catch {
      setTokensSummary(null);
    }
  };

  const loadLogs = async (forSlug) => {
    try {
      const url = forSlug
        ? `/api/super-admin/push/logs?tenant=${encodeURIComponent(forSlug)}&limit=50`
        : `/api/super-admin/push/logs?limit=50`;
      const { data } = await api.get(url);
      setLogs(data.logs || []);
    } catch {
      setLogs([]);
    }
  };

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    // Wrap loader in a named inner helper to satisfy
    // react-hooks/set-state-in-effect (per pattern used in SuperPages.jsx).
    const loadForSlug = async () => {
      setDiagnosis(null);
      // "__all__" = show every token across every tenant (diagnostic).
      const tenantQuery = slug === "__all__" ? "" : `?tenant=${encodeURIComponent(slug)}`;
      try {
        const { data } = await api.get(`/api/super-admin/push/tokens${tenantQuery}`);
        if (!cancelled) setTokensSummary(data);
      } catch { if (!cancelled) setTokensSummary(null); }
      try {
        const logsQuery = slug === "__all__" ? "?limit=50" : `?tenant=${encodeURIComponent(slug)}&limit=50`;
        const { data } = await api.get(`/api/super-admin/push/logs${logsQuery}`);
        if (!cancelled) setLogs(data.logs || []);
      } catch { if (!cancelled) setLogs([]); }
    };
    loadForSlug();
    return () => { cancelled = true; };
  }, [slug]);

  const runDiagnosis = async () => {
    if (!slug || slug === "__all__") return toast.error("Pick a specific tenant to diagnose");
    setDiagnosing(true);
    try {
      const { data } = await api.get(`/api/super-admin/push/diagnose/${encodeURIComponent(slug)}`);
      setDiagnosis(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Diagnosis failed");
    } finally {
      setDiagnosing(false);
    }
  };

  // Estimated matching devices from the by-persona / by-platform rollup
  const estimatedTargets = useMemo(() => {
    if (!tokensSummary) return 0;
    if (persona === "all" && platform === "all") return tokensSummary.total || 0;
    // Conservative best-effort: min of persona and platform counts.
    const pCount = persona === "all" ? tokensSummary.total : (tokensSummary.by_persona?.[persona] || 0);
    const platCount = platform === "all" ? tokensSummary.total : (tokensSummary.by_platform?.[platform] || 0);
    return Math.min(pCount, platCount);
  }, [tokensSummary, persona, platform]);

  const send = async () => {
    if (!slug || slug === "__all__") return toast.error("Pick a specific tenant to send");
    if (!title.trim() || !body.trim()) return toast.error("Title and body required");
    setSending(true);
    try {
      const { data } = await api.post("/api/super-admin/push/send", {
        tenant_slug: slug,
        persona, platform,
        title: title.trim(),
        body: body.trim(),
        deep_link: deepLink.trim() || null,
        user_phone: userPhone.trim() || null,
      });
      if (data.total === 0) {
        toast.warning("No devices matched the filter — running diagnosis…");
        await runDiagnosis();
      } else if (data.failed === 0) {
        toast.success(`Sent to ${data.successful}/${data.total} devices`);
      } else {
        toast.warning(`Partial: ${data.successful} ok, ${data.failed} failed, ${data.stale_cleared} stale tokens removed`);
      }
      await loadLogs(slug);
      await loadTokens(slug);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6 page-enter" data-testid="super-push-page">
      <div>
        <div className="text-xs uppercase tracking-wider font-bold text-brand-clay">Notifications</div>
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight flex items-center gap-2">
          <Bell className="w-6 h-6 text-brand-sky"/> Push Notifications (FCM)
        </h1>
        <p className="text-sm text-neutral-500 mt-1">
          Send notifications to native customer or rider apps. Filter by tenant, persona, platform — every send is audit-logged.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
        {/* Compose card */}
        <div className="lg:col-span-3 bg-white border border-neutral-200 rounded-2xl p-5 space-y-4" data-testid="push-compose-card">
          <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-neutral-700">
            <Send className="w-4 h-4"/> Compose
          </div>

          <FieldRow label="Tenant">
            <select
              data-testid="push-tenant-select"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              className="w-full h-10 px-3 rounded-lg border border-neutral-300 bg-white text-sm">
              <option value="__all__">— ALL tenants (diagnostic view) —</option>
              {tenants.length === 0 && <option value="">— no tenants —</option>}
              {tenants.map((t) => (
                <option key={t.slug} value={t.slug}>{t.name} ({t.slug})</option>
              ))}
            </select>
          </FieldRow>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <FieldRow label="Persona">
              <select
                data-testid="push-persona-select"
                value={persona}
                onChange={(e) => setPersona(e.target.value)}
                className="w-full h-10 px-3 rounded-lg border border-neutral-300 bg-white text-sm">
                {personaOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </FieldRow>
            <FieldRow label="Platform">
              <select
                data-testid="push-platform-select"
                value={platform}
                onChange={(e) => setPlatform(e.target.value)}
                className="w-full h-10 px-3 rounded-lg border border-neutral-300 bg-white text-sm">
                {platformOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </FieldRow>
          </div>

          <FieldRow label={`Title (${title.length}/120)`}>
            <Input
              data-testid="push-title-input"
              value={title}
              maxLength={120}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Your milk order is on the way"
            />
          </FieldRow>

          <FieldRow label={`Body (${body.length}/500)`}>
            <textarea
              data-testid="push-body-input"
              value={body}
              maxLength={500}
              onChange={(e) => setBody(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 rounded-lg border border-neutral-300 text-sm resize-y min-h-[80px]"
              placeholder="Suresh will arrive between 6:30 AM – 7:00 AM"
            />
          </FieldRow>

          <FieldRow label="Deep link (optional)">
            <Input
              data-testid="push-deeplink-input"
              value={deepLink}
              onChange={(e) => setDeepLink(e.target.value)}
              placeholder="/app/orders/abc123"
            />
          </FieldRow>

          <FieldRow label="One-to-one test phone (optional)">
            <Input
              data-testid="push-phone-input"
              value={userPhone}
              onChange={(e) => setUserPhone(e.target.value)}
              placeholder="+919876543210"
            />
          </FieldRow>

          <div className="flex items-center justify-between pt-2 border-t border-neutral-100">
            <div className="text-xs text-neutral-500 flex items-center gap-1.5">
              <Users className="w-3.5 h-3.5"/>
              ~{estimatedTargets} device{estimatedTargets === 1 ? "" : "s"} match
            </div>
            <div className="flex items-center gap-2">
              <Button
                data-testid="push-diagnose-btn"
                onClick={runDiagnosis}
                disabled={diagnosing || !slug || slug === "__all__"}
                variant="outline"
                className="border-neutral-300">
                {diagnosing ? <RefreshCw className="w-4 h-4 mr-1.5 animate-spin"/> : <AlertTriangle className="w-4 h-4 mr-1.5"/>}
                Diagnose
              </Button>
              <Button
                data-testid="push-send-btn"
                onClick={send}
                disabled={sending || !slug || slug === "__all__" || !title.trim() || !body.trim()}
                className="bg-brand-sky hover:bg-brand-sky/90 text-white">
                {sending ? <RefreshCw className="w-4 h-4 mr-1.5 animate-spin"/> : <Send className="w-4 h-4 mr-1.5"/>}
                {sending ? "Sending…" : (slug === "__all__" ? "Pick a tenant first" : "Send")}
              </Button>
            </div>
          </div>
        </div>

        {/* Tokens / devices summary */}
        <div className="lg:col-span-2 bg-white border border-neutral-200 rounded-2xl p-5 space-y-4" data-testid="push-tokens-card">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-neutral-700">
              <Smartphone className="w-4 h-4"/> Registered devices
            </div>
            <button
              onClick={() => loadTokens(slug)}
              data-testid="push-tokens-refresh-btn"
              className="text-xs text-brand-sky hover:underline">
              Refresh
            </button>
          </div>
          {!tokensSummary ? (
            <LoadingState size="sm" />
          ) : tokensSummary.total === 0 ? (
            <EmptyState icon={Smartphone} size="sm"
                        title={`No devices registered${slug === "__all__" ? " for any tenant" : " for this tenant"} yet`}
                        description="Native apps register on first launch after permission grant." />
          ) : (
            <>
              <div className="text-3xl font-extrabold tracking-tight" data-testid="push-tokens-total">{tokensSummary.total}</div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="space-y-1">
                  <div className="font-bold text-neutral-500 uppercase tracking-wider">By persona</div>
                  {Object.entries(tokensSummary.by_persona || {}).map(([k, v]) => (
                    <div key={k} className="flex justify-between"><span className="capitalize">{k}</span><span className="font-bold">{v}</span></div>
                  ))}
                </div>
                <div className="space-y-1">
                  <div className="font-bold text-neutral-500 uppercase tracking-wider">By platform</div>
                  {Object.entries(tokensSummary.by_platform || {}).map(([k, v]) => (
                    <div key={k} className="flex justify-between"><span className="capitalize">{k}</span><span className="font-bold">{v}</span></div>
                  ))}
                </div>
              </div>
              {/* Per-token diagnostic table — useful for spotting wrong-tenant
                  registrations. Capped at 20 rows; refresh to update. */}
              {(tokensSummary.tokens || []).length > 0 && (
                <div className="pt-2 border-t border-neutral-100">
                  <div className="text-xs font-bold text-neutral-500 uppercase tracking-wider mb-1.5">
                    Recent devices ({Math.min(20, tokensSummary.tokens.length)})
                  </div>
                  <div className="max-h-64 overflow-auto -mx-1">
                    <table className="w-full text-xs">
                      <thead className="text-neutral-500">
                        <tr>
                          <th className="text-left px-1 py-1">Persona</th>
                          <th className="text-left px-1 py-1">Platform</th>
                          {slug === "__all__" && <th className="text-left px-1 py-1">Tenant</th>}
                          <th className="text-left px-1 py-1">Phone</th>
                          <th className="text-left px-1 py-1">Token</th>
                          <th className="text-left px-1 py-1">Updated</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tokensSummary.tokens.slice(0, 20).map((tok) => (
                          <tr key={tok.id} className="border-t border-neutral-100">
                            <td className="px-1 py-1 capitalize">{tok.persona}</td>
                            <td className="px-1 py-1">{tok.platform}</td>
                            {slug === "__all__" && (
                              <td className="px-1 py-1 font-mono text-[10px]">{tok.tenant_slug || tok.tenant_id?.slice(-6) || "—"}</td>
                            )}
                            <td className="px-1 py-1 font-mono text-[10px]">{tok.phone || "—"}</td>
                            <td className="px-1 py-1 font-mono text-[10px]">…{tok.token_tail}</td>
                            <td className="px-1 py-1 text-neutral-400 text-[10px]">
                              {tok.updated_at ? new Date(tok.updated_at).toLocaleString() : "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Diagnosis panel — shown when operator clicks Diagnose or when a
          send hits "0 devices matched". Surfaces every common failure mode
          for the push pipeline so you can pinpoint what's broken. */}
      {diagnosis && (
        <div className="bg-white border border-neutral-200 rounded-2xl p-5 space-y-4" data-testid="push-diagnosis-panel">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-bold uppercase tracking-wider text-neutral-700 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-500"/> Diagnosis · {diagnosis.tenant_slug}
            </h2>
            <button
              onClick={() => setDiagnosis(null)}
              className="text-xs text-neutral-500 hover:underline"
              data-testid="push-diagnosis-close">Dismiss</button>
          </div>

          {/* Hints first — these are the actionable next steps */}
          <div className="space-y-2">
            {(diagnosis.hints || []).map((h, i) => (
              <div key={i} className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900"
                   data-testid={`push-diagnosis-hint-${i}`}>
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0"/>
                <div>{h}</div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Firebase state */}
            <div className="space-y-2">
              <div className="text-xs font-bold uppercase tracking-wider text-neutral-500">Firebase provisioning</div>
              <div className="text-xs">
                <span className="text-neutral-500">project_id:</span>{" "}
                <code className="font-mono text-[11px]" data-testid="push-diagnosis-project-id">
                  {diagnosis.firebase_project_id || "— (NONE) —"}
                </code>
              </div>
              <table className="w-full text-xs">
                <tbody>
                  {Object.entries(diagnosis.firebase_apps || {}).map(([k, v]) => (
                    <tr key={k} className="border-t border-neutral-100">
                      <td className="py-1 font-mono text-[11px]">{k}</td>
                      <td className="py-1">
                        {v.state === "ok"
                          ? <span className="text-brand-leaf font-bold inline-flex items-center gap-1">
                              <CheckCircle2 className="w-3 h-3"/> ok
                            </span>
                          : <span className="text-amber-700 font-bold inline-flex items-center gap-1">
                              <AlertTriangle className="w-3 h-3"/> {v.state}
                            </span>}
                      </td>
                      <td className="py-1 font-mono text-[10px] text-neutral-500">
                        {v.package_name || v.detail || ""}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Device tokens breakdown */}
            <div className="space-y-2">
              <div className="text-xs font-bold uppercase tracking-wider text-neutral-500">Registered devices ({diagnosis.device_token_total})</div>
              {diagnosis.device_token_total === 0 ? (
                <EmptyState icon={Smartphone} size="sm" title="No devices registered"
                            description="No devices have called /api/push/register for this tenant yet." className="py-4" />
              ) : (
                <table className="w-full text-xs">
                  <tbody>
                    {Object.entries(diagnosis.device_tokens_by_persona_platform || {}).map(([k, v]) => (
                      <tr key={k} className="border-t border-neutral-100">
                        <td className="py-1 font-mono text-[11px]">{k}</td>
                        <td className="py-1 text-right font-bold">{v}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {diagnosis.most_recent_token_update && (
                <div className="text-[10px] text-neutral-400">
                  Most recent registration: {new Date(diagnosis.most_recent_token_update).toLocaleString()}
                </div>
              )}

              {/* Rider users vs token coverage — pinpoints "logged in but no token" */}
              {diagnosis.rider_users_total > 0 && (
                <div className="pt-2 mt-2 border-t border-neutral-100" data-testid="push-diagnosis-rider-users">
                  <div className="text-xs font-bold uppercase tracking-wider text-neutral-500 mb-1">Rider login vs token</div>
                  <div className="text-xs">
                    <span className="font-bold text-brand-leaf">{diagnosis.rider_users_with_token}</span>
                    <span className="text-neutral-500"> with token · </span>
                    <span className="font-bold text-amber-700">{diagnosis.rider_users_total - diagnosis.rider_users_with_token}</span>
                    <span className="text-neutral-500"> logged in but no token</span>
                  </div>
                  {(diagnosis.rider_users_without_token || []).length > 0 && (
                    <div className="text-[10px] text-neutral-400 mt-1 max-h-20 overflow-auto">
                      {diagnosis.rider_users_without_token.map((u, i) => (
                        <div key={i} className="font-mono">{u.phone || "—"} {u.name ? `(${u.name})` : ""}</div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Recent push attempts (compact) */}
          {(diagnosis.recent_push_logs || []).length > 0 && (
            <div>
              <div className="text-xs font-bold uppercase tracking-wider text-neutral-500 mb-1.5">Last 5 send attempts</div>
              <table className="w-full text-xs">
                <thead className="text-neutral-500">
                  <tr>
                    <th className="text-left py-1">When</th>
                    <th className="text-left">Title</th>
                    <th className="text-left">Result</th>
                    <th className="text-left">First error(s)</th>
                  </tr>
                </thead>
                <tbody>
                  {diagnosis.recent_push_logs.map((L) => (
                    <tr key={L.id} className="border-t border-neutral-100">
                      <td className="py-1 text-neutral-500 text-[10px]">{L.created_at ? new Date(L.created_at).toLocaleString() : "—"}</td>
                      <td className="py-1 truncate max-w-[180px]">{L.title}</td>
                      <td className="py-1">{L.successful}/{L.total}{L.failed > 0 && <span className="text-amber-600"> ({L.failed} fail)</span>}</td>
                      <td className="py-1 text-[10px] text-neutral-500">{(L.first_errors || []).join(" · ") || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Audit logs */}
      <div className="bg-white border border-neutral-200 rounded-2xl p-5 space-y-3" data-testid="push-logs-card">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-bold uppercase tracking-wider text-neutral-700">Audit log (last 50)</h2>
          <button
            onClick={() => loadLogs(slug)}
            data-testid="push-logs-refresh-btn"
            className="text-xs text-brand-sky hover:underline">
            Refresh
          </button>
        </div>
        {logs.length === 0 ? (
          <EmptyState icon={Send} size="sm" title="No sends yet for this tenant" />
        ) : (
          <div className="divide-y divide-neutral-100">
            {logs.map((l) => {
              const open = expandedLog === l.id;
              const ok = l.failed === 0 && l.total > 0;
              return (
                <div key={l.id} className="py-3" data-testid={`push-log-row-${l.id}`}>
                  <button
                    onClick={() => setExpandedLog(open ? null : l.id)}
                    className="w-full text-left flex items-start gap-3">
                    <div className="mt-0.5">
                      {open ? <ChevronDown className="w-4 h-4 text-neutral-400"/> : <ChevronRight className="w-4 h-4 text-neutral-400"/>}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        {ok
                          ? <CheckCircle2 className="w-4 h-4 text-brand-leaf shrink-0"/>
                          : <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0"/>}
                        <div className="font-bold text-sm truncate">{l.title}</div>
                      </div>
                      <div className="text-xs text-neutral-500 mt-0.5 truncate">{l.body}</div>
                      <div className="text-xs text-neutral-400 mt-0.5">
                        {new Date(l.created_at).toLocaleString()} · {l.tenant_slug} ·
                        {" "}{l.successful}/{l.total} sent
                        {l.failed > 0 && <span className="text-amber-600 font-bold"> · {l.failed} failed</span>}
                        {l.stale_cleared > 0 && <span className="text-neutral-500"> · {l.stale_cleared} stale cleared</span>}
                      </div>
                    </div>
                  </button>
                  {open && (
                    <div className="mt-3 ml-7 bg-neutral-50 rounded-lg p-3 text-xs space-y-2">
                      <div><span className="font-bold">Filters:</span> {JSON.stringify(l.filters)}</div>
                      {l.deep_link && <div><span className="font-bold">Deep link:</span> {l.deep_link}</div>}
                      {(l.results || []).length > 0 && (
                        <div className="max-h-60 overflow-auto">
                          <table className="w-full text-xs">
                            <thead className="text-neutral-500">
                              <tr><th className="text-left py-1">Token</th><th className="text-left">Result</th><th className="text-left">Note</th></tr>
                            </thead>
                            <tbody>
                              {l.results.map((r, i) => (
                                <tr key={i} className="border-t border-neutral-200">
                                  <td className="py-1 font-mono">…{r.token_tail}</td>
                                  <td>{r.success ? <span className="text-brand-leaf">OK</span> : <span className="text-amber-600">FAIL</span>}</td>
                                  <td className="text-neutral-500">{r.stale ? "stale (removed)" : (r.error || "—")}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function FieldRow({ label, children }) {
  return (
    <label className="block space-y-1.5">
      <div className="text-xs uppercase tracking-wider font-bold text-neutral-500">{label}</div>
      {children}
    </label>
  );
}
